/* eslint-disable react-hooks/exhaustive-deps */
import { bracketOptionTeam } from '@/commonFunctions/bracketActions'
import { has } from '@/commonFunctions/utils'
import { Button, ButtonPrimary } from '@/components/button'
import {
  Checkmark,
  Close,
  FilterNoBg,
  InfoIconOutline,
  More,
  People,
  Person,
  PersonAdd,
  RemoveCircle,
  UserIconFilled,
} from '@/components/icons'
import { ConfirmModal, NotifyModal } from '@/components/modal'
import { BracketTeamItemLoading } from '@/components/skeleton'
import { BRACKET_MODIFY, LIMIT, OFFSET } from '@/constants/Common'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import {
  useBracketConfirmTeam,
  useBracketEditTeam,
  useBracketSwitchTeams,
  useGetBracketTeams,
  useGetBracketWaitListTeams,
  useSwitchWaitListed,
} from '@/services'
import { BracketPlayer, BracketTeam, Order } from '@/types'
import { Menu, Transition } from '@headlessui/react'
import { isEmpty } from 'lodash'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'
import { eventSelector } from '../selectors'
import { AddPartnerModal } from './AddPartnerModal'
import { ReplaceTeamModal } from './ReplaceTeamModal'
import { SelectBracketModal } from './SelectBracketModal'

type Props = {
  permissions: string[]
}

export const PlayerItem = ({ player }: { player: BracketPlayer }) => {
  return (
    <>
      <div className="flex gap-x-2">
        {player?.imageUrl ? (
          <img src={player?.imageUrl} className="rounded-full w-7 h-7" alt="" />
        ) : (
          <UserIconFilled className="w-7 h-7 text-primary-500" />
        )}
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-navy-900">{player?.fullName}</span>
          <span className="text-xs text-navy-900 text-opacity-60">
            {player?.age}
            {player?.gender ? ` • ${player?.gender[0]}` : ''}
            {player?.shortAddress ? `• ${player.shortAddress}` : ''}
          </span>
        </div>
      </div>
      <div className="flex gap-x-1 items-center">
        <div className="bg-navy-900 bg-opacity-5 rounded-lg px-2 w-11 h-9  flex flex-col justify-center items-center">
          <People className="text-navy-900" />
          <span className="text-xs text-navy-900 font-medium">{player?.ratings?.doubles}</span>
        </div>
        <div className="bg-navy-900 bg-opacity-5 rounded-lg px-2 w-10 h-9  flex flex-col justify-center items-center">
          <Person className="text-navy-900" />
          <span className="text-xs text-navy-900 font-medium">{player?.ratings?.singles}</span>
        </div>
      </div>
    </>
  )
}

type BracketTeamClone = BracketTeam & { failedReason?: string }

type ConfirmModalType = 'EDIT' | 'CONFIRM' | ''
type OpenSearchType = 'player1' | 'player2' | ''
type OpenSwitchBracketType = 'STEP1' | 'STEP2' | ''

export const BracketTeams = ({ permissions }: Props) => {
  const { t } = useTranslation()

  const params = useParams()
  const { bracket, event } = useAppSelector(eventSelector)
  const { token } = useAppSelector(authSelector)

  const isMobile = useMediaQuery('(max-width: 767px)')

  const { eventId, bracketId } = params

  const { mutate: getTeams, data: resTeams, isLoading } = useGetBracketTeams()
  const { mutate: getWaitListTeams, data: resWaiListTeams } = useGetBracketWaitListTeams()
  const { mutate: editTeam } = useBracketEditTeam()
  const { mutate: switchBrackets, data: resBracketSwitchTeams } = useBracketSwitchTeams()
  const { mutate: confirmTeam } = useBracketConfirmTeam()
  const { mutate: switchWaitList } = useSwitchWaitListed()

  const [offset, setOffset] = useState(OFFSET)
  const [teams, setTeams] = useState<BracketTeamClone[]>([])
  const [order, setOrder] = useState<Order | ''>()
  const [supportTeam, setSupportTeam] = useState(false)
  const [confirmModal, setConfirmModal] = useState<{ registrationId?: number; type: ConfirmModalType }>({ type: '' })
  const [switchBracket, setSwitchBracket] = useState<OpenSwitchBracketType>('')
  const [replaceTeam, setReplaceTeam] = useState<OpenSwitchBracketType>('')
  const [addPartner, setAddPartner] = useState<OpenSearchType>('')
  const [excludePlayerId, setExcludePlayerId] = useState<number | undefined>()
  const [editData, setEditData] = useState<{
    registrationIdEdit: number | null
    player1: BracketPlayer | null
    player2: BracketPlayer | null
    isPlayer1ClubMember?: boolean
    isPlayer2ClubMember?: boolean
  }>({
    registrationIdEdit: null,
    player1: {} as BracketPlayer,
    player2: {} as BracketPlayer,
  })
  const [switchBracketData, setSwitchBracketData] = useState<any>([])
  const [availableBrackets, setAvailableBrackets] = useState<any>([])
  const [targetBracket, setTargetBracket] = useState<any>()
  const [lastElement, setLastElement] = useState<any>(null)
  const [replaceTeamData, setReplaceTeamData] = useState<any>(null)

  const showWithdraw =
    bracket?.durationStatus !== 'COMPLETE' && (bracket?.elimination === 'COMPASS' ? !bracket?.canShowStandings : true)

  const openSupportTeamModal = () => setSupportTeam(true)

  const closeSupportTeamModal = () => setSupportTeam(false)

  const openAddPartnerModal = (key: OpenSearchType) => {
    if (key === 'player1') {
      setExcludePlayerId(editData?.player2?.id || undefined)
    } else {
      setExcludePlayerId(editData?.player1?.id || undefined)
    }
    setAddPartner(key)
  }

  const closeAddPartnerModal = () => setAddPartner('')

  const openConfirmModal = (type: ConfirmModalType, registrationId?: number) =>
    setConfirmModal({
      type: type,
      ...(registrationId && {
        registrationId: registrationId,
      }),
    })

  const closeConfirmModal = () => setConfirmModal({ type: '' })

  const openSwitchBracketModal = (step: OpenSwitchBracketType) => setSwitchBracket(step)

  const closeSwitchBracketModal = () => {
    setSwitchBracket('')
    setTargetBracket(null)
  }

  const openReplaceTeamModal = (step: OpenSwitchBracketType) => setReplaceTeam(step)

  const closeReplaceTeamModal = () => setReplaceTeam('')

  const switchTeamsBracket = (team: BracketTeam) => {
    const checkTeams = switchBracketData.find((item: any) => item.registrationId === team.registrationId) || []
    if (isEmpty(checkTeams) && bracket) {
      setSwitchBracketData([
        ...switchBracketData,
        {
          clubId: bracket.clubId,
          eventId,
          eventName: bracket.leagueName,
          player1: team.player1.id,
          player2: team.player2.id,
          reSeedBracket: true,
          registrationId: team.registrationId,
          sourceBracketId: bracketId,
          sourceBracketName: bracket.name,
          targetBracketId: '',
          targetBracketName: '',
          gender: [team.player1.gender, team.player2.gender],
          age: [team.player1.age, team.player2.age],
        },
      ])
    } else {
      const switchData = switchBracketData.filter((item: any) => {
        return item.registrationId !== team.registrationId
      })
      setSwitchBracketData(switchData)
    }
  }

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const BgColor = (value: string) => {
    if (value === 'CONFIRMED') return 'bg-green'
    if (value === 'NOT_CONFIRMED') return 'bg-warn'
    return 'bg-slate-500'
  }

  const ContentColor = (value: string) => {
    if (value === 'CONFIRMED') return 'text-green'
    if (value === 'NOT_CONFIRMED') return 'text-warn'
    return 'text-slate-500'
  }

  const EventStatusCase = (value: string) => {
    if (value === 'CONFIRMED') return t('event.teamConfirmed')
    if (value === 'NOT_CONFIRMED') return t('event.teamPending')
    return value
  }

  const onChangeOrder = (type: Order) => {
    if (order !== type || offset !== OFFSET) {
      setOrder(type)
      setTeams([])
      setOffset(OFFSET)
    }
  }

  const onClearFilter = () => {
    if (order !== '' || offset !== OFFSET) {
      setOrder('')
      setTeams([])
      setOffset(OFFSET)
    }
  }

  const onGetTeams = () => {
    if (!isMobile && bracket && token) {
      getTeams({
        format: bracket.format,
        bracketId: Number(bracketId),
        limit: LIMIT,
        offset: offset,
        ...(order && {
          sort: {
            order: order,
            parameter: 'RATINGS',
          },
        }),
      })
    }
  }

  const onEditTeam = (registrationId: number, team: BracketTeam) =>
    setEditData({
      player1: team.player1,
      player2: team.player2,
      registrationIdEdit: registrationId,
    })

  const onCancelEditTeam = () =>
    setEditData({
      registrationIdEdit: null,
      player1: {} as BracketPlayer,
      player2: {} as BracketPlayer,
    })

  const onConfirmTeam = (registrationId?: number) => {
    if (bracketId && registrationId)
      confirmTeam(
        {
          bracketId: bracketId,
          registrationId: registrationId,
        },
        {
          onSuccess: (data) => {
            closeConfirmModal()
            setTeams([])
            toast.success(data.message)
            onGetTeams()
          },
        }
      )
  }

  const onSplitTeam = (registrationId: number) => {
    if (bracketId && bracket)
      editTeam(
        {
          bracketId: bracketId,
          clubId: bracket?.clubId.toString(),
          player1: null,
          player2: null,
          registrationId: registrationId,
        },
        {
          onSuccess: () => {
            setTeams([])
            toast.success('Team withdrawn Successfully!')
            onGetTeams()
          },
        }
      )
  }

  const onConfirmEditTeam = () => {
    if (bracketId && bracket && editData?.player1?.id && editData?.player2?.id && editData?.registrationIdEdit)
      editTeam(
        {
          bracketId: bracketId,
          clubId: bracket?.clubId.toString(),
          player1: editData?.player1?.id,
          player2: editData?.player2.id,
          registrationId: editData?.registrationIdEdit,
          ...(editData.isPlayer1ClubMember !== undefined && {
            isPlayer1ClubMember: editData.isPlayer1ClubMember,
          }),
          ...(editData.isPlayer2ClubMember !== undefined && {
            isPlayer2ClubMember: editData.isPlayer2ClubMember,
          }),
        },
        {
          onSuccess: () => {
            closeConfirmModal()
            setEditData({
              registrationIdEdit: null,
              player1: {} as BracketPlayer,
              player2: {} as BracketPlayer,
            })
            setTeams([])
            onGetTeams()
            toast.success(t('common.success'))
          },
          onError: (error: any) => {
            toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
          },
        }
      )
  }

  const onConfirmSwitchBracket = () => {
    switchBrackets(switchBracketData, {
      onSuccess: () => {
        closeSwitchBracketModal()
        setTeams([])
        setSwitchBracketData([])
        onGetTeams()
        toast.success(
          `The selected teams were successfully move to the following brackets and matches are reseeded accordingly. : ${switchBracketData?.[0].targetBracketName}`
        )
      },
    })
  }

  const switchTeamDataWaitlisted = () => {
    if (bracket && bracketId) {
      const payload = {
        bracketId,
        clubId: bracket?.clubId?.toString(),
        teamSource: {
          player1Id: replaceTeamData[0].player1.id,
          player2Id: replaceTeamData[0].player2.id,
        },
        teamTarget: {
          player1Id: replaceTeamData[1].player1.id,
          player2Id: replaceTeamData[1].player2.id,
        },
      }
      switchWaitList(payload, {
        onSuccess: () => {
          toast.success('The selected team was replaced successfully and matches are reseeded accordingly.')
          setReplaceTeamData(null)
          setTeams([])
          onGetTeams()
        },
        onError: () => {
          toast.error(APP_CUSTOM_ERROR)
        },
      })
    }
  }

  useEffect(() => {
    if (!isMobile && bracket && token) {
      getTeams({
        format: bracket.format,
        bracketId: Number(bracketId),
        limit: LIMIT,
        offset: offset,
        ...(order && {
          sort: {
            order: order,
            parameter: 'RATINGS',
          },
        }),
      })
      getWaitListTeams({
        format: bracket.format,
        bracketId: Number(bracketId),
        limit: 20,
        offset: 0,
        onlyNonWaitListed: false,
        onlyWaitListed: true,
      })
    }
  }, [bracket, offset, order])

  useEffect(() => {
    if (resTeams && resBracketSwitchTeams) {
      const formatTeams = resTeams.result.hits.reduce((arr, item) => {
        const findResultSwitch = resBracketSwitchTeams.results.find((ele) => ele.registrationId === item.registrationId)
        if (findResultSwitch) {
          arr.push({ ...item, ...(findResultSwitch?.failedReason && { failedReason: findResultSwitch.failedReason }) })
        }
        if (!findResultSwitch) arr.push(item)
        return arr
      }, [] as BracketTeamClone[])

      setTeams([...teams, ...formatTeams])
    }
    if (resTeams && !resBracketSwitchTeams) {
      setTeams([...teams, ...resTeams.result.hits])
    }
  }, [resTeams])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  useEffect(() => {
    if (bracket && event) {
      const eligibleBrackets = bracketOptionTeam(bracket, switchBracketData, event?.brackets)
      setAvailableBrackets(eligibleBrackets)
    }
  }, [switchBracketData, event, bracket])

  useEffect(() => {
    if (targetBracket) {
      const newData = switchBracketData.map((item: any) => {
        return {
          ...item,
          targetBracketId: targetBracket?.bracketId,
          targetBracketName: targetBracket?.name,
        }
      })
      setSwitchBracketData(newData)
    }
  }, [targetBracket])

  const getEditTeamMessage = useMemo(() => {
    if (confirmModal.type === 'EDIT') {
      const selectedTeam = teams.find((item) => item.registrationId === editData.registrationIdEdit)
      const playerNames: string[] = []
      if (selectedTeam?.player1?.id !== editData.player1?.id && selectedTeam?.player1?.fullName) {
        playerNames.push(selectedTeam.player1.fullName)
      }
      if (selectedTeam?.player2?.id !== editData.player2?.id && selectedTeam?.player2?.fullName) {
        playerNames.push(selectedTeam.player2.fullName)
      }
      if (playerNames.length) {
        return `${t('event.confirmWithdrawPlayerFirstText')}
        ${playerNames.length === 1 ? playerNames[0] : `${playerNames[0]} ${t('event.and')} ${playerNames[1]}`}
        ${t('event.confirmWithdrawPlayerSecondText')}\n
        ${t('event.moveToUnmatchedTab')}`
      }
    }
    return t('event.confirmEditTeam')
  }, [confirmModal.type, teams, editData])

  return (
    <>
      <div className="mt-4 flex flex-col gap-y-4">
        <div className="flex justify-between">
          <div className="flex gap-x-2 sm:gap-x-4 items-center justify-between sm:justify-start">
            <div className="flex gap-x-2 sm:gap-x-4 items-center">
              <Menu as="div" className="relative inline-block">
                <div>
                  <Menu.Button className="flex gap-x-1 items-center bg-primary-500 rounded-full hover:bg-opacity-70 px-3 py-1 text-xs font-semibold text-white">
                    {t('common.sort')}
                    <FilterNoBg className="w-4 h-4" />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute mt-2 w-48 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="px-1 py-1 ">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`${
                              active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                            } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                            onClick={() => onChangeOrder('DESC')}
                          >
                            <Checkmark className={`${order === 'DESC' ? 'opacity-100' : 'opacity-0'}`} />
                            {t('common.highToLowRating')}
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`${
                              active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                            } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                            onClick={() => onChangeOrder('ASC')}
                          >
                            <Checkmark className={`${order === 'ASC' ? 'opacity-100' : 'opacity-0'}`} />
                            {t('common.lowToHighRating')}
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
            <button
              className="p-1 flex items-center justify-center hover:bg-navy-900 hover:bg-opacity-20 rounded-full"
              onClick={openSupportTeamModal}
            >
              <InfoIconOutline className="w-4 h-4 text-navy-900" />
            </button>
            <Button
              className="text-xs text-white hidden sm:inline-block font-semibold hover:text-opacity-70"
              onClick={onClearFilter}
            >
              {t('common.clearFilters')}
            </Button>
            <Button className="inline-block sm:hidden" onClick={onClearFilter}>
              <Close className="text-white hover:text-opacity-70" />
            </Button>
          </div>
          <div>
            {has(permissions, BRACKET_MODIFY) && (
              <ButtonPrimary
                title={t('event.switchBracket')}
                disabled={isEmpty(switchBracketData)}
                onClick={() => openSwitchBracketModal('STEP1')}
              />
            )}
          </div>
        </div>
        {teams?.map((item, index) => {
          const teamIndex = index + 1
          const checked = switchBracketData.find(
            (switchBracketData: any) => switchBracketData.registrationId === item.registrationId
          )

          const hideReplace =
            item?.player1?.registrationDetails?.player1?.isWaitListed ||
            item?.player2?.registrationDetails?.player2?.isWaitListed

          const disableReplace = bracket?.hasConfirmMatch || !resWaiListTeams?.result.hits.length

          const isEditing = item.registrationId === editData.registrationIdEdit
          return (
            <div
              className="flex bg-white rounded-2xl p-4 gap-x-4"
              key={item?.registrationId}
              ref={index === teams.length - 1 && resTeams?.result.hasMore ? setLastElement : null}
            >
              {has(permissions, BRACKET_MODIFY) && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    className={`w-5 h-5`}
                    checked={!!checked}
                    onChange={() => switchTeamsBracket(item)}
                  />
                </div>
              )}
              <div className="flex flex-1 flex-col gap-y-3">
                <div className="flex justify-between">
                  <span className="text-[11px] font-medium text-navy-900 text-opacity-50">Team {teamIndex}</span>
                  <div className="flex gap-x-2">
                    <p
                      className={`${BgColor(item?.partnerStatus)} ${ContentColor(
                        item?.partnerStatus
                      )} bg-opacity-20 rounded-lg px-2 h-6 flex items-center font-medium text-xs`}
                    >
                      {EventStatusCase(item?.partnerStatus)}
                    </p>

                    {has(permissions, BRACKET_MODIFY) && bracket?.durationStatus !== 'COMPLETE' && (
                      <Menu as="div" className="relative inline-block text-left">
                        <Menu.Button>
                          <More className="w-6 h-6" />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 mt-2 w-32 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="flex flex-col gap-y-1 px-1 py-1 ">
                              {item.partnerStatus === 'NOT_CONFIRMED' && (
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                                        active && 'bg-slate-300'
                                      } `}
                                      onClick={() => {
                                        openConfirmModal('CONFIRM', item.registrationId)
                                      }}
                                    >
                                      {t('event.confirmTeam')}
                                    </button>
                                  )}
                                </Menu.Item>
                              )}
                              {(item.partnerStatus === 'CONFIRMED' ||
                                item.partnerStatus === 'NOT_CONFIRMED' ||
                                item.partnerStatus === 'INVITED') &&
                                showWithdraw &&
                                !bracket?.hasConfirmMatch && (
                                  <>
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                                            active && 'bg-slate-300'
                                          } `}
                                          onClick={() => {
                                            onEditTeam(item.registrationId, item)
                                          }}
                                        >
                                          {t('event.editTeam')}
                                        </button>
                                      )}
                                    </Menu.Item>
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                                            active && 'bg-slate-300'
                                          } `}
                                          onClick={() => {
                                            onSplitTeam(item.registrationId)
                                          }}
                                        >
                                          {t('event.splitTeam')}
                                        </button>
                                      )}
                                    </Menu.Item>
                                  </>
                                )}
                              {item.partnerStatus === 'CONFIRMED' && !hideReplace && (
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                                        disableReplace ? 'opacity-50' : active ? 'bg-slate-300' : ''
                                      } `}
                                      disabled={disableReplace}
                                      onClick={() => {
                                        setReplaceTeamData([item])
                                        openReplaceTeamModal('STEP1')
                                      }}
                                    >
                                      {t('event.replace')}
                                    </button>
                                  )}
                                </Menu.Item>
                              )}
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    )}
                  </div>
                </div>
                <div className="flex gap-x-6">
                  <div className="flex flex-col gap-y-3 flex-1">
                    <div
                      className={`flex justify-between bg-white ${isEditing && 'border p-4 rounded relative'} ${
                        !editData.player1 && 'border-primary-500 hover:bg-primary-500 hover:bg-opacity-5'
                      }`}
                    >
                      {isEditing ? (
                        !editData.player1 ? (
                          <div
                            className="flex flex-1 py-[6px] items-center justify-center gap-x-4 cursor-pointer"
                            onClick={() => openAddPartnerModal('player1')}
                          >
                            <PersonAdd className="text-primary-500" />
                            <span className="text-primary-500 font-semibold text-sm">{t('event.addAPartner')}</span>
                          </div>
                        ) : (
                          <PlayerItem player={editData?.player1} />
                        )
                      ) : (
                        <PlayerItem player={item?.player1} />
                      )}
                      {isEditing && editData.player1 && (
                        <button
                          className="absolute -top-2 -right-2"
                          onClick={() =>
                            setEditData({
                              ...editData,
                              player1: null,
                            })
                          }
                        >
                          <RemoveCircle />
                        </button>
                      )}
                    </div>

                    <div
                      className={`flex justify-between bg-white ${isEditing && 'border p-4 rounded relative'} ${
                        !editData.player2 && 'border-primary-500 hover:bg-primary-500 hover:bg-opacity-5'
                      }`}
                    >
                      {isEditing ? (
                        !editData.player2 ? (
                          <div
                            className="flex flex-1 py-[6px] items-center justify-center gap-x-4 cursor-pointer"
                            onClick={() => openAddPartnerModal('player2')}
                          >
                            <PersonAdd className="text-primary-500" />
                            <span className="text-primary-500 font-semibold text-sm">{t('event.addAPartner')}</span>
                          </div>
                        ) : (
                          <PlayerItem player={editData?.player2} />
                        )
                      ) : (
                        <PlayerItem player={item?.player2} />
                      )}
                      {isEditing && editData.player2 && (
                        <button
                          className="absolute -top-2 -right-2"
                          onClick={() =>
                            setEditData({
                              ...editData,
                              player2: null,
                            })
                          }
                        >
                          <RemoveCircle />
                        </button>
                      )}
                    </div>
                  </div>
                  {has(permissions, BRACKET_MODIFY) && bracket?.durationStatus !== 'COMPLETE' && isEditing && (
                    <div className="flex flex-col gap-y-4 items-center justify-center">
                      <button
                        className="text-xs text-navy-900 font-semibold border shadow-md px-2 py-1 rounded w-full hover:bg-slate-100"
                        onClick={onCancelEditTeam}
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        className="text-xs text-navy-900 font-semibold border shadow-md px-2 py-1 rounded w-full hover:bg-slate-100"
                        onClick={() => openConfirmModal('EDIT')}
                      >
                        {t('common.saveChanges')}
                      </button>
                    </div>
                  )}
                </div>
                {item.failedReason && <div className="flex text-boldOrange text-sm">{item.failedReason}</div>}
              </div>
            </div>
          )
        })}
        {isLoading && (
          <>
            <BracketTeamItemLoading className="!max-w-[964px]" />
            <BracketTeamItemLoading className="!max-w-[964px]" />
            <BracketTeamItemLoading className="!max-w-[964px]" />
            <BracketTeamItemLoading className="!max-w-[964px]" />
            <BracketTeamItemLoading className="!max-w-[964px]" />
            <BracketTeamItemLoading className="!max-w-[964px]" />
          </>
        )}

        {resTeams && !resTeams?.result.hasMore && (
          <div className="flex justify-center">
            <span className="text-white font-semibold text-opacity-60">{t('common.noMoreResults')}</span>
          </div>
        )}
      </div>
      <NotifyModal
        open={supportTeam}
        onClose={closeSupportTeamModal}
        title={t('common.sortingRules')}
        desc={t('common.sortingRulesDesc')}
        onSubmit={closeSupportTeamModal}
      />
      {bracket && (
        <AddPartnerModal
          open={!!addPartner}
          onClose={closeAddPartnerModal}
          currentBracketData={bracket}
          showUnclaimedAccounts
          individualBracket={editData}
          excludePlayerId={excludePlayerId}
          onSubmit={(value) => {
            setEditData({
              ...editData,
              [addPartner]: value.player,
              ...(addPartner === 'player1' && {
                isPlayer1ClubMember: value.isPlayerClubMember,
              }),
              ...(addPartner === 'player2' && {
                isPlayer2ClubMember: value.isPlayerClubMember,
              }),
            })
            setAddPartner('')
          }}
          bracketId={bracketId}
        />
      )}
      <SelectBracketModal
        open={switchBracket === 'STEP1'}
        onClose={closeSwitchBracketModal}
        title="Switch Brackets"
        eligibleBrackets={availableBrackets}
        targetBracket={targetBracket}
        setTargetBracket={setTargetBracket}
        onSubmit={() => setSwitchBracket('STEP2')}
        disabledSubmitBtn={!targetBracket}
      />
      <ReplaceTeamModal
        open={replaceTeam === 'STEP1'}
        onClose={closeReplaceTeamModal}
        title="Replace Team"
        waitListTeams={resWaiListTeams?.result?.hits}
        onSubmit={(value) => {
          setReplaceTeamData([...replaceTeamData, value])
          closeReplaceTeamModal()
        }}
      />
      <ConfirmModal
        open={switchBracket === 'STEP2' || !!confirmModal.type}
        onClose={() => {
          if (switchBracket === 'STEP2') closeSwitchBracketModal()
          if (confirmModal.type) closeConfirmModal()
        }}
        onCloseButton={() => {
          if (switchBracket === 'STEP2') setSwitchBracket('STEP1')
          if (confirmModal.type) closeConfirmModal()
        }}
        title={switchBracket === 'STEP2' ? 'Confirmation' : confirmModal.type === 'EDIT' ? 'Edit team' : 'Confirm Team'}
        desc={
          switchBracket === 'STEP2'
            ? t('event.switchBracketsConfirmation')
            : confirmModal.type === 'EDIT'
              ? getEditTeamMessage
              : t('event.confirmThisTeams')
        }
        onSubmit={() => {
          if (switchBracket === 'STEP2') onConfirmSwitchBracket()
          if (confirmModal.type === 'EDIT') onConfirmEditTeam()
          if (confirmModal.type === 'CONFIRM') onConfirmTeam(confirmModal.registrationId)
        }}
        cancelTitle={t('common.back')}
        submitTitle={t('common.confirm')}
      />

      {/* Confirm for Replace Team */}
      <ConfirmModal
        open={replaceTeamData?.length === 2}
        onClose={() => setReplaceTeamData(null)}
        title="Confirmation"
        desc="Are you sure you want to replace this team with the selected team?"
        onSubmit={switchTeamDataWaitlisted}
        cancelTitle={t('common.back')}
        submitTitle={t('common.confirm')}
      />
    </>
  )
}
